import { Link } from 'react-router-dom';

interface HeroSectionProps {
  badge: string;
  headline: string;
  headlineAccent: string;
  headlineSuffix?: string;
  subtitle: string;
  primaryCTA: { text: string; href: string };
  secondaryCTA: { text: string; href: string };
  trustIndicators?: string[];
  imageSrc: string;
}

export default function HeroSection({
  badge,
  headline,
  headlineAccent,
  headlineSuffix,
  subtitle,
  primaryCTA,
  secondaryCTA,
  trustIndicators = ['Licensed & Insured', '10-Year Warranty', 'Same-Day Service'],
  imageSrc,
}: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-white">
      {/* Background image with light overlay — fills right side */}
      <div className="absolute inset-0">
        <img
          src={imageSrc}
          alt="Security system installation professionals"
          className="w-full h-full object-cover object-center"
          style={{ opacity: 0.18 }}
        />
        {/* Very light overlay to keep text legible */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white/30" />
      </div>

      {/* Right side — large image block */}
      <div className="absolute right-0 top-0 bottom-0 w-5/12 hidden xl:block overflow-hidden">
        <img
          src={imageSrc}
          alt="Professional security camera installation"
          className="w-full h-full object-cover object-top"
        />
        {/* Gradient fade from left */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/30 to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-32 pb-20">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-orange-50 border border-orange-200 mb-8">
            <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
            <span className="text-orange-700 text-sm font-semibold">{badge}</span>
          </div>

          {/* H1 */}
          <h1 className="font-display text-5xl lg:text-[66px] font-black leading-[1.05] mb-6 text-slate-900">
            {headline}{' '}
            <span className="text-gradient-orange">{headlineAccent}</span>
            {headlineSuffix && (
              <>
                {' '}
                <span className="text-slate-900">{headlineSuffix}</span>
              </>
            )}
          </h1>

          {/* Subtitle */}
          <p className="text-slate-600 text-xl leading-relaxed mb-10 max-w-xl">
            {subtitle}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 mb-12">
            <Link
              to={primaryCTA.href}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {primaryCTA.text}
              <i className="ri-arrow-right-line text-base" />
            </Link>
            <a
              href={secondaryCTA.href}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-slate-200 bg-white text-slate-900 font-bold text-sm hover:border-orange-300 hover:text-orange-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-orange-500" />
              </div>
              {secondaryCTA.text}
            </a>
          </div>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {trustIndicators.map((item) => (
              <div key={item} className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-orange-100 flex-shrink-0">
                  <i className="ri-shield-check-fill text-orange-600 text-xs" />
                </div>
                <span className="text-slate-600 text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Floating stats — right side of left section */}
        <div className="absolute bottom-20 right-[43%] hidden xl:flex flex-col gap-3">
          {[
            { value: '500+', label: 'Properties Secured', icon: 'ri-building-fill' },
            { value: '15+', label: 'Years Experience', icon: 'ri-history-fill' },
            { value: '24hr', label: 'Install Turnaround', icon: 'ri-timer-flash-fill' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 px-5 py-4 rounded-xl bg-white border border-slate-200 shadow-sm"
            >
              <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-orange-100 flex-shrink-0">
                <i className={`${stat.icon} text-orange-600 text-lg`} />
              </div>
              <div>
                <p className="text-slate-900 font-display font-black text-xl leading-tight">{stat.value}</p>
                <p className="text-slate-500 text-xs">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
